import React, { useContext, useState, useRef, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'

import { OEContext } from '@aytm/app/stats/page/comments'

import { decodeText } from '@aytm/app/stats/lib/helpers'

import style from '@aytm/app/stats/page/comments/comments/styles/OEItem.module.scss'

let statusTimer = null
let videoTimer = null
let videoInterval = null

const formatTime = totalSeconds => {
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = Math.floor(totalSeconds % 60)

  const formattedMinutes = String(minutes).padStart(2, '0')
  const formattedSeconds = String(seconds).padStart(2, '0')

  return `${formattedMinutes}:${formattedSeconds}`
}

const VideoResponse = ({ node, path, responseId, downloadUrl, isRejected, onUpdateParams }) => {
  const { searchState } = useContext(OEContext)

  const { thumbnail_url, mp4File } = node.v
  const { isQAMode, isShowAllTools, isVideoResponsesMode } = searchState

  const [copied, setCopied] = useState(false)

  const [currentTime, setCurrentTime] = useState(0)
  const [isShowVideo, setIsShowVideo] = useState(false)

  const videoRef = useRef(null)

  const onShowVideo = () => {
    setIsShowVideo(true)

    videoTimer = setTimeout(() => videoRef.current.play(), 100)
  }

  const onCopyClick = () => {
    if (statusTimer) clearTimeout(statusTimer)

    const text = decodeText(node.timings.map(timing => timing[2]).join('\r\n'))

    if (window.navigator?.clipboard) {
      const type = 'text/plain'
      const blob = new Blob([text], { type })
      const data = [new ClipboardItem({ [type]: blob })]
      window.navigator.clipboard.write(data)
    } else {
      const textarea = document.createElement('textarea')
      textarea.value = text
      textarea.style.position = 'fixed'
      textarea.style.left = '-999999px'
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
    }

    setCopied(true)
  }

  const onTimingClick = time => {
    setIsShowVideo(true)
    setCurrentTime(time)
  }

  const detectCurrentTiming = useCallback(() => {
    const timingNodes = document.querySelectorAll(`.video-response-${responseId}-timing`)
    for (const timingNode of timingNodes) {
      timingNode.classList.remove(style['comment-answer-video-timing-current'])
    }

    const index = node.timings.findIndex(
      timing => timing[0] <= videoRef.current.currentTime && videoRef.current.currentTime < timing[1]
    )

    if (timingNodes[index]) {
      timingNodes[index].classList.add(style['comment-answer-video-timing-current'])
    }
  }, [node.timings, responseId])

  const onPlayingStart = useCallback(() => {
    if (Array.isArray(node.timings) && 0 < node.timings.length && isVideoResponsesMode) {
      if (videoInterval) clearInterval(videoInterval)
      videoInterval = setInterval(() => detectCurrentTiming(), 100)
    }
  }, [detectCurrentTiming])
  const onPlayingEnd = useCallback(() => {
    if (Array.isArray(node.timings) && 0 < node.timings.length && isVideoResponsesMode) {
      if (videoInterval) clearInterval(videoInterval)
      detectCurrentTiming()
    }
  }, [detectCurrentTiming])

  const onResponseClick = () => {
    onUpdateParams({ query: [{ value: responseId, label: responseId, type: 'uid' }] }, undefined, 'video-responses')
  }

  useEffect(() => {
    if (videoRef.current && Array.isArray(node.timings) && 0 < node.timings.length && isVideoResponsesMode) {
      videoRef.current.currentTime = currentTime
      detectCurrentTiming()
    }
  }, [currentTime, detectCurrentTiming])

  useEffect(() => {
    if (statusTimer) clearTimeout(statusTimer)
    if (videoTimer && !isShowVideo) clearTimeout(videoTimer)

    const videoNode = videoRef.current

    if (videoNode) {
      videoNode.addEventListener('playing', onPlayingStart)
      videoNode.addEventListener('pause', onPlayingEnd)
    }

    return () => {
      if (videoNode) {
        videoNode.removeEventListener('playing', onPlayingStart)
        videoNode.removeEventListener('pause', onPlayingEnd)
      }
    }
  }, [isShowVideo, onPlayingStart, onPlayingEnd])

  useEffect(() => {
    if (statusTimer) clearTimeout(statusTimer)

    if (copied) {
      statusTimer = setTimeout(() => setCopied(false), 1500)
    }

    return () => {
      if (statusTimer) clearTimeout(statusTimer)
    }
  }, [copied])

  return (
    <div
      className={`${style['comment-answer-video']}${
        isVideoResponsesMode ? ' ' + style['comment-answer-video-active'] : ''
      }`}
    >
      {isShowVideo ? (
        <>
          <div className={style['comment-answer-video-settings']}>
            {null !== downloadUrl && !isQAMode && !isShowAllTools && !isRejected ? (
              <a
                className="p-button p-button-rounded p-button-stat"
                href={downloadUrl}
                download={`AYTM-Survey-${window.surveyId}-Video-Response-${responseId}_${path}.mp4`}
                target="_blank"
                rel="noreferrer"
              >
                DOWNLOAD
              </a>
            ) : null}
            {isVideoResponsesMode ? null : (
              <span className={style['resize-button']} onClick={() => setIsShowVideo(!isShowVideo)}>
                <span className={style['resize-button-left']} dangerouslySetInnerHTML={{ __html: '&nearr;' }} />
                <span className={style['resize-button-right']} dangerouslySetInnerHTML={{ __html: '&swarr;' }} />
              </span>
            )}
          </div>
          <video ref={videoRef} width="100%" height="auto" poster={thumbnail_url} controls>
            <source src={mp4File} />
          </video>
        </>
      ) : (
        <div className={style['comment-answer-video-poster']} onClick={onShowVideo}>
          <div className={style['comment-answer-video-settings']}>
            {isVideoResponsesMode ? null : (
              <span className={style['resize-button']} onClick={() => setIsShowVideo(!isShowVideo)}>
                <span className={style['resize-button-left']} dangerouslySetInnerHTML={{ __html: '&swarr;' }} />
                <span className={style['resize-button-right']} dangerouslySetInnerHTML={{ __html: '&nearr;' }} />
              </span>
            )}
          </div>
          <img src={thumbnail_url} />
        </div>
      )}
      {Array.isArray(node.timings) && 0 < node.timings.length ? (
        isVideoResponsesMode ? (
          <div className={style['comment-answer-video-timings']}>
            <div className={style['comment-answer-video-timings-title']}>
              <b>Transcript (auto-generated)</b>
              <span className={style['comment-answer-video-timings-copy']} onClick={copied ? null : onCopyClick}>
                <i className="icon-aytmfd-copy" />
                {copied ? 'copied!' : 'copy transcript'}
              </span>
            </div>
            {node.timings.map((timing, index) => (
              <div
                key={`timing-${index + 1}`}
                className={`${style['comment-answer-video-timing']} video-response-${responseId}-timing`}
              >
                <span
                  className={style['comment-answer-video-timing-timestamp']}
                  onClick={() => onTimingClick(timing[0])}
                >
                  {formatTime(timing[0])}
                </span>
                <span className={style['comment-answer-video-timing-text']}>{timing[2]}</span>
              </div>
            ))}
          </div>
        ) : (
          <>
            <div className={style['comment-answer-video-description']} onClick={onResponseClick}>
              <span className={style['comment-answer-video-description-open']}>Open transcript with time codes</span>
              <div className={style['comment-answer-video-description-text']}>{node.t}</div>
            </div>
          </>
        )
      ) : null}
    </div>
  )
}

VideoResponse.propTypes = {
  node: PropTypes.object,
  path: PropTypes.string,
  responseId: PropTypes.number,
  downloadUrl: PropTypes.string,
  isRejected: PropTypes.bool,
  onUpdateParams: PropTypes.func
}

VideoResponse.displayName = 'VideoResponse'

export default VideoResponse
